import type { SenderGroupOutput } from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderGroups } from "@/services";
import {
	type StatusTranslation,
	getSenderPriorityTranslation,
} from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	DataTable,
	useDataTableRowSelection,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { type FC, useMemo } from "react";
import SenderGroupsDeleteAction from "./SenderGroupsDeleteAction";
import SenderGroupsSingleAction from "./SenderGroupsSingleAction";

type SenderGroupRow = SenderGroupOutput;

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const SenderGroupsSection: FC = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = usePersistedServerTableState("sender-groups");

	const rowSelection = useDataTableRowSelection<SenderGroupRow>(true);

	const {
		data: groups,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetSenderGroups({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
		},
	});

	const columns = useMemo<ColumnDef<SenderGroupRow>[]>(
		() => [
			{
				id: "name",
				header: t("senderGroup.tableName"),
				enableSorting: false,
				cell: ({ row }) => row.original.name,
			},
			{
				id: "to_keep",
				header: t("senderGroup.tableToKeep"),
				enableSorting: false,
				cell: ({ row }) => row.original.to_keep ?? "-",
			},
			{
				id: "priority_usage",
				header: t("sender.tablePriority"),
				enableSorting: false,
				cell: ({ row }) => {
					if (!row.original.priority_usage) {
						return (
							<Chip variant="muted" size="sm">
								{t("sender.resolvedPriorityLabelGlobal")}
							</Chip>
						);
					}
					const { color, text } = getSenderPriorityTranslation(
						row.original.priority_usage,
						t,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "timezone",
				header: t("senderGroup.tableTimezone"),
				enableSorting: false,
				cell: ({ row }) => row.original.timezone ?? "-",
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<SenderGroupsSingleAction selectedGroup={row.original} />
				),
			},
		],
		[t],
	);

	return (
		<div className="mb-10 flex flex-col gap-4">
			<SenderGroupsDeleteAction
				selectedIds={rowSelection.selectedIds}
				onClear={rowSelection.clear}
			/>

			<DataTable
				columns={columns}
				data={groups?.results ?? []}
				totalItems={groups?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.id}
				rowSelection={rowSelection}
			/>
		</div>
	);
};

export default SenderGroupsSection;
